import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import Sidebar from "../../components/Dashboard/Sidebar";
import Layout from "../../components/Layout/Layout";
import { useAuth } from "../../context/auth";
import StatCard from "../../components/Dashboard/StatsCard";

import {
  FiSearch,
  FiPlus,
  FiGift,
  FiCheckCircle,
  FiClock,
  FiPercent,
} from "react-icons/fi";

const AdminCoupon = () => {
  const [auth] = useAuth();

  const [loading, setLoading] = useState(false);

  const [coupons, setCoupons] = useState([]);

  const [search, setSearch] = useState("");

  const [filter, setFilter] = useState("all");

  const [editingCoupon, setEditingCoupon] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);

  const [form, setForm] = useState({
    code: "",
    discountPercent: "",
    minAmount: "",
    expiryDate: "",

    targetType: "ALL",

    users: [],

    group: "",

    notifyUsers: true,

    autoAssignToNewUsers: false,
  });
  const [users, setUsers] = useState([]);

  const [userSearch, setUserSearch] = useState("");
  const getUsers = async () => {
    try {
      const { data } = await axios.get("/api/v1/auth/all-users", headers);

      if (data.success) {
        setUsers(data.users);
      }
    } catch (err) {
      console.log(err);
    }
  };
  useEffect(() => {
    if (auth?.token) {
      getCoupons();
      getUsers();
    }
  }, [auth]);

  const headers = {
    headers: {
      Authorization: `Bearer ${auth?.token}`,
    },
  };

  // GET COUPONS

  const getCoupons = async () => {
    try {
      setLoading(true);

      const { data } = await axios.get("/api/v1/coupon", headers);

      if (data?.success) {
        setCoupons(data.coupons);
      }
    } catch (err) {
      console.log(err);
      toast.error("Failed to fetch coupons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth?.token) {
      getCoupons();
    }
  }, [auth]);

  // FILTERED COUPONS

  const filteredCoupons = useMemo(() => {
    let data = [...coupons];

    if (search) {
      data = data.filter((coupon) =>
        coupon.code.toLowerCase().includes(search.toLowerCase()),
      );
    }

    if (filter === "active") {
      data = data.filter((coupon) => new Date(coupon.expiryDate) > new Date());
    }

    if (filter === "expired") {
      data = data.filter((coupon) => new Date(coupon.expiryDate) <= new Date());
    }

    return data;
  }, [coupons, search, filter]);

  // STATS

  const totalCoupons = coupons.length;

  const activeCoupons = coupons.filter(
    (coupon) => new Date(coupon.expiryDate) > new Date(),
  ).length;

  const expiredCoupons = coupons.filter(
    (coupon) => new Date(coupon.expiryDate) <= new Date(),
  ).length;

  const averageDiscount =
    coupons.length > 0
      ? Math.round(
          coupons.reduce((sum, coupon) => sum + coupon.discountPercent, 0) /
            coupons.length,
        )
      : 0;

  // RETURN

  return (
    <Layout title="Coupon Management">
      <div className="min-h-screen bg-gray-50 mt-20">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">
              Coupon Management
            </h1>

            <p className="text-gray-600 mt-1.5">
              Create and manage store coupons
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            <aside className="w-full lg:w-72 flex-shrink-0">
              <Sidebar
                auth={auth}
                activeTab="coupons"
                setActiveTab={() => {}}
                setSidebarOpen={() => {}}
                onLogout={() => {
                  localStorage.removeItem("auth");
                  window.location.reload();
                }}
              />
            </aside>

            {/* Main Content */}
            <main className="flex-1 min-w-0">
              {/* Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <StatCard
                  icon={FiGift}
                  title="Total Coupons"
                  value={totalCoupons}
                  subtitle="Coupons created"
                  color="#1a1a1a"
                />

                <StatCard
                  icon={FiCheckCircle}
                  title="Active Coupons"
                  value={activeCoupons}
                  subtitle="Currently active"
                  color="#16522d"
                />

                <StatCard
                  icon={FiClock}
                  title="Expired"
                  value={expiredCoupons}
                  subtitle="Need renewal"
                  color="#dc2626"
                  alert={expiredCoupons > 0}
                />

                <StatCard
                  icon={FiPercent}
                  title="Avg Discount"
                  value={`${averageDiscount}%`}
                  subtitle="Average offer"
                  color="#d97706"
                />
              </div>

              {/* CREATE COUPON */}

              <div className="bg-white rounded-2xl shadow-sm border mb-8">
                <div className="px-6 py-5 border-b">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-slate-200 flex items-center justify-center">
                      <FiPlus className="text-xl" color="#020202" />
                    </div>

                    <div>
                      <h2 className="text-xl font-bold">Create Coupon</h2>

                      <p className="text-gray-500 text-sm">
                        Create discount coupons for customers.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid lg:grid-cols-2 gap-6">
                    {/* Coupon Code */}

                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        Coupon Code
                      </label>

                      <input
                        type="text"
                        value={form.code}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            code: e.target.value.toUpperCase(),
                          })
                        }
                        placeholder="WELCOME20"
                        className="w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-green-700 outline-none"
                      />
                    </div>

                    {/* Discount */}

                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        Discount (%)
                      </label>

                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={form.discountPercent}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            discountPercent: e.target.value,
                          })
                        }
                        placeholder="20"
                        className="w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-green-700 outline-none"
                      />
                    </div>

                    {/* Minimum Order */}

                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        Minimum Order Amount
                      </label>

                      <input
                        type="number"
                        value={form.minAmount}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            minAmount: e.target.value,
                          })
                        }
                        placeholder="1000"
                        className="w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-green-700 outline-none"
                      />
                    </div>

                    {/* Expiry */}

                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        Expiry Date
                      </label>

                      <input
                        type="date"
                        value={form.expiryDate}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            expiryDate: e.target.value,
                          })
                        }
                        className="w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-green-700 outline-none"
                      />
                    </div>
                    <div className="lg:col-span-2 border rounded-2xl p-6">
                      <h3 className="text-lg font-bold mb-5">
                        Coupon Audience
                      </h3>

                      <select
                        value={form.targetType}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            targetType: e.target.value,
                          })
                        }
                        className="w-full border rounded-xl px-4 py-3"
                      >
                        <option value="ALL">Everyone</option>

                        <option value="SPECIFIC_USERS">Specific Users</option>

                        <option value="NEW_USERS">New Users</option>
                      </select>
                    </div>
                    {form.targetType === "SPECIFIC_USERS" && (
                      <div className="lg:col-span-2 border rounded-2xl p-6 mt-6">
                        <h4 className="text-base font-semibold mb-4">
                          Select Users
                        </h4>

                        <div className="max-h-72 overflow-y-auto rounded-xl border divide-y">
                          {users.length === 0 ? (
                            <div className="p-6 text-center text-gray-500">
                              No Users Found
                            </div>
                          ) : (
                            users.map((user) => (
                              <label
                                key={user._id}
                                className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition"
                              >
                                <div>
                                  <h5 className="font-semibold text-gray-900">
                                    {user.name}
                                  </h5>

                                  <p className="text-sm text-gray-500">
                                    {user.email}
                                  </p>
                                </div>

                                <input
                                  type="checkbox"
                                  checked={form.users.includes(user._id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setForm({
                                        ...form,
                                        users: [...form.users, user._id],
                                      });
                                    } else {
                                      setForm({
                                        ...form,
                                        users: form.users.filter(
                                          (id) => id !== user._id,
                                        ),
                                      });
                                    }
                                  }}
                                  className="w-5 h-5 accent-black"
                                />
                              </label>
                            ))
                          )}
                        </div>

                        <div className="mt-4 text-sm text-gray-500">
                          Selected Users:
                          <span className="font-semibold text-black ml-1">
                            {form.users.length}
                          </span>
                        </div>
                      </div>
                    )}

                    {form.targetType === "NEW_USERS" && (
                      <div className="lg:col-span-2 mt-6">
                        <label className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={form.autoAssignToNewUsers}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                autoAssignToNewUsers: e.target.checked,
                              })
                            }
                          />
                          Automatically assign to every new registered user
                        </label>
                      </div>
                    )}
                    <div className="lg:col-span-2 mt-6">
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={form.notifyUsers}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              notifyUsers: e.target.checked,
                            })
                          }
                        />
                        Notify eligible users about this coupon
                      </label>
                    </div>
                  </div>

                  {/* Create Button */}

                  <div className="mt-8 flex justify-end">
                    <button
                      onClick={async () => {
                        try {
                          if (
                            !form.code ||
                            !form.discountPercent ||
                            !form.expiryDate
                          ) {
                            return toast.error(
                              "Please fill all required fields",
                            );
                          }

                          setLoading(true);

                          const { data } = await axios.post(
                            "/api/v1/coupon/create-coupon",
                            {
                              code: form.code,
                              discountPercent: Number(form.discountPercent),
                              expiryDate: form.expiryDate,
                              minAmount: Number(form.minAmount) || 0,

                              targetType: form.targetType,
                              users: form.users,
                              group: form.group,
                              notifyUsers: form.notifyUsers,
                              autoAssignToNewUsers: form.autoAssignToNewUsers,
                            },
                            headers,
                          );

                          if (data.success) {
                            toast.success("Coupon Created Successfully");

                            setForm({
                              code: "",
                              discountPercent: "",
                              minAmount: "",
                              expiryDate: "",

                              targetType: "ALL",
                              users: [],
                              group: "",
                              notifyUsers: true,
                              autoAssignToNewUsers: false,
                            });

                            getCoupons();
                          } else {
                            toast.error(data.message);
                          }
                        } catch (error) {
                          console.log(error);

                          toast.error(
                            error.response?.data?.message ||
                              "Something went wrong",
                          );
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                      className="px-7 py-3 rounded-xl text-white font-semibold transition-all duration-300 hover:scale-[1.02] disabled:opacity-60"
                      style={{
                        background: "#0c0c0c",
                      }}
                    >
                      {loading ? "Creating..." : "Create Coupon"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Search & Filter */}

              <div className="bg-white rounded-2xl shadow-sm border p-5 mb-8">
                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="relative flex-1">
                    <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

                    <input
                      type="text"
                      placeholder="Search coupon..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full border rounded-xl pl-12 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-700"
                    />
                  </div>

                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="border rounded-xl px-4 py-3 outline-none"
                  >
                    <option value="all">All Coupons</option>

                    <option value="active">Active</option>

                    <option value="expired">Expired</option>
                  </select>
                </div>
              </div>

              {/* COUPON TABLE */}

              <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
                <div className="px-6 py-5 border-b">
                  <h2 className="text-xl font-bold">All Coupons</h2>
                </div>

                {loading ? (
                  <div className="py-20 text-center text-gray-500">
                    Loading coupons...
                  </div>
                ) : filteredCoupons.length === 0 ? (
                  <div className="py-20 text-center text-gray-400">
                    <FiGift className="mx-auto text-6xl mb-4" />

                    <p>No Coupons Found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-5 py-4 text-left">Code</th>

                          <th className="px-5 py-4 text-left">Discount</th>

                          <th className="px-5 py-4 text-left">Min Order</th>

                          <th className="px-5 py-4 text-left">Expiry</th>

                          <th className="px-5 py-4 text-left">Status</th>

                          <th className="px-5 py-4 text-center">Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {filteredCoupons.map((coupon) => (
                          <tr
                            key={coupon._id}
                            className="border-b hover:bg-gray-50 transition"
                          >
                            {/* Coupon Code */}
                            <td className="px-5 py-4">
                              <div className="font-semibold text-gray-900">
                                {coupon.code}
                              </div>
                            </td>

                            {/* Discount */}
                            <td className="px-5 py-4">
                              <span className="font-semibold text-green-700">
                                {coupon.discountPercent}%
                              </span>
                            </td>

                            {/* Minimum Order */}
                            <td className="px-5 py-4">
                              ₹{coupon.minAmount?.toLocaleString()}
                            </td>

                            {/* Expiry */}
                            <td className="px-5 py-4">
                              {new Date(coupon.expiryDate).toLocaleDateString()}
                            </td>

                            {/* Status */}
                            <td className="px-5 py-4">
                              {new Date(coupon.expiryDate) > new Date() ? (
                                <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                                  Active
                                </span>
                              ) : (
                                <span className="px-3 py-1 rounded-full bg-red-100 text-red-600 text-xs font-semibold">
                                  Expired
                                </span>
                              )}
                            </td>

                            {/* Actions */}
                            <td className="px-5 py-4">
                              <div className="flex justify-center gap-3">
                                <button
                                  onClick={() => {
                                    setEditingCoupon(coupon);

                                    setForm({
                                      code: coupon.code,
                                      discountPercent: coupon.discountPercent,
                                      minAmount: coupon.minAmount,
                                      expiryDate: coupon.expiryDate.slice(
                                        0,
                                        10,
                                      ),

                                      targetType: coupon.targetType,
                                      users: coupon.users || [],
                                      group: coupon.group || "",
                                      notifyUsers: coupon.notifyUsers,
                                      autoAssignToNewUsers:
                                        coupon.autoAssignToNewUsers,
                                    });

                                    setShowEditModal(true);
                                  }}
                                  className="px-4 py-2 rounded-lg text-sm font-medium text-white transition hover:scale-105"
                                  style={{
                                    background: "#000000",
                                  }}
                                >
                                  Edit
                                </button>

                                <button
                                  onClick={async () => {
                                    if (!window.confirm("Delete this coupon?"))
                                      return;

                                    try {
                                      await axios.delete(
                                        `/api/v1/coupon/${coupon._id}`,
                                        headers,
                                      );

                                      toast.success(
                                        "Coupon Deleted Successfully",
                                      );

                                      getCoupons();
                                    } catch (error) {
                                      toast.error("Delete Failed");
                                    }
                                  }}
                                  className="px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {showEditModal && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                  <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
                    <div className="border-b px-6 py-4">
                      <h2 className="text-xl font-bold">Edit Coupon</h2>
                    </div>

                    <div className="p-6 space-y-5">
                      <div>
                        <label className="font-medium block mb-2">
                          Coupon Code
                        </label>

                        <input
                          type="text"
                          value={form.code}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              code: e.target.value.toUpperCase(),
                            })
                          }
                          className="w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-green-700"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="font-medium block mb-2">
                            Discount %
                          </label>

                          <input
                            type="number"
                            value={form.discountPercent}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                discountPercent: e.target.value,
                              })
                            }
                            className="w-full border rounded-xl px-4 py-3 outline-none"
                          />
                        </div>

                        <div>
                          <label className="font-medium block mb-2">
                            Min Amount
                          </label>

                          <input
                            type="number"
                            value={form.minAmount}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                minAmount: e.target.value,
                              })
                            }
                            className="w-full border rounded-xl px-4 py-3 outline-none"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="font-medium block mb-2">
                          Expiry Date
                        </label>

                        <input
                          type="date"
                          value={form.expiryDate}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              expiryDate: e.target.value,
                            })
                          }
                          className="w-full border rounded-xl px-4 py-3 outline-none"
                        />
                      </div>

                      <div className="flex justify-end gap-3 pt-4">
                        <button
                          onClick={() => {
                            setShowEditModal(false);
                            setEditingCoupon(null);
                          }}
                          className="px-6 py-3 rounded-xl border"
                        >
                          Cancel
                        </button>

                        <button
                          onClick={async () => {
                            try {
                              await axios.put(
                                `/api/v1/coupon/${editingCoupon._id}`,
                                {
                                  code: form.code,
                                  discountPercent: Number(form.discountPercent),
                                  minAmount: Number(form.minAmount),
                                  expiryDate: form.expiryDate,

                                  targetType: form.targetType,
                                  users: form.users,
                                  group: form.group,
                                  notifyUsers: form.notifyUsers,
                                  autoAssignToNewUsers:
                                    form.autoAssignToNewUsers,
                                },
                                headers,
                              );

                              toast.success("Coupon Updated");

                              setShowEditModal(false);

                              setEditingCoupon(null);

                              getCoupons();
                            } catch (error) {
                              toast.error("Update Failed");
                            }
                          }}
                          className="px-6 py-3 rounded-xl text-white"
                          style={{
                            background: "#030303",
                          }}
                        >
                          Save Changes
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminCoupon;
